/**
****************************************************************************************
Project: Readabl
File: SearchComponent.js
Description: This component will have the design and controls for Pdf search box.
© Healthcare Triangle, Inc
****************************************************************************************
**/

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDownward, ArrowUpward, Search, Close } from "@mui/icons-material";
import "./SearchComponent.css";
import { CircularProgress } from "@mui/material";

//  this debounce function will call only after specific time. not for all input change
function debounce(func) {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), 500);
    };
}

const SearchComponent = (props) => {
    const inputRef = useRef(null);
    const [toggleSearchBox, updateSearchToggle] = useState(false);
    const [currentIndex, updateCurrentIndex] = useState(0);
    const [inputValue, updateInputValue] = useState('');
    const [loader, updateLoader] = useState(false);

    useEffect(() => {
        updateLoader(false);
    }, [props.searchResults]);

    useEffect(() => {
        updateSearchToggle(false);
    }, [props.pdfUrl]);

    useEffect(() => {
        if(!toggleSearchBox) {
            updateCurrentIndex(0);
            updateInputValue("");
            typeof props.onSearchClose == "function" && props.onSearchClose();
        } else {
            inputRef.current.focus();
        }
    }, [toggleSearchBox]);

    useEffect(() => {
        if(props.searchString) {
            updateLoader(true);
            updateSearchToggle(true);
            typeof props.handleSearch == "function" && props.handleSearch(props.searchString);
        }

        updateInputValue(props.searchString || "");
    }, [props.searchString]);

    const handleSearchButton = () => {
        typeof props.onSearchOpen == "function" && props.onSearchOpen();
        updateSearchToggle(!toggleSearchBox);
    }

    const debounceSearch = useCallback(
        debounce((value) => {
            updateLoader(true);
            typeof props.handleSearch == "function" && props.handleSearch(value);
        }), []);

    //  handle input search text
    const handleInputChange = (e) => {
        const { value = "" } = e.target;
        updateCurrentIndex(0);
        updateInputValue(value);
        debounceSearch(value);
    }

    // navigating backword through search text
    const stepBackWord = () => {
        if(currentIndex > 0) {
            let index = currentIndex - 1
            updateCurrentIndex(index);
            typeof props.displaySearchText == "function" && props.displaySearchText(props.searchResults, index);
        }
    }

    // navigating forward through search text
    const stepForword = () => {
        if(currentIndex < props.searchResults?.length - 1) {
            let index = currentIndex + 1
            updateCurrentIndex(index);
            typeof props.displaySearchText == "function" && props.displaySearchText(props.searchResults, index);
        }
    }


    return (
        <div class={`bb-search-container ${toggleSearchBox ? " active" : ""}` }>
            <input type="text" ref={inputRef} value={inputValue} placeholder="Search..." onChange={handleInputChange} />
            <div className="search-count-container">
                {loader ? <CircularProgress sx={{width: "20px !important", height: "20px !important"}}/> : <span>{props.searchResults?.length ? currentIndex + 1 : 0}/{props.searchResults?.length || 0}</span>}
                <span style={{ margin: "0px 8px" }}>|</span>
                <div className="count-label">
                    <ArrowUpward onClick={stepBackWord} />
                    <ArrowDownward onClick={stepForword} />
                </div>
            </div>
            <button class={`search-btn`} onClick={handleSearchButton}>
                {toggleSearchBox ? <Close /> : <Search /> }
            </button>
        </div>
    )
}

export default SearchComponent;